import { HttpResponse } from '@angular/common/http';
import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';
import { ProgressIndicatorService } from '@services/progress';

import { I18nService } from '@localize/services';
import { IAppKey } from '@services/appkey';
import { ICompany } from '@services/company';
import { DialogService } from '@services/dialog';
import { IAppRequiredTerms, ISortedAppTerm, LegalsService, TermState, TermType } from '@services/legals';
import { AppKeyAssociateLegalDialogComponent } from '../appkey-associate-legal-dialog';

@Component({
	selector: 'app-appkey-legals-table',
	templateUrl: './appkey-legals-table.component.html',
	styleUrls: ['./appkey-legals-table.component.scss']
})
export class AppKeyLegalsTableComponent implements OnInit, AfterViewInit {
	@Input() editable = true;
	@Input() company: ICompany;
	@Input() appKey: IAppKey;
	@Input() terms: IAppRequiredTerms;

	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(MatTable) table: MatTable<any>;

	public loading = true;
	public openedMenu: number;
	public tableColumns: string[] = ['displayName', 'version', 'alias', 'action'];
	public mergedTerms: ISortedAppTerm[] = [];
	public associatedTerms: ISortedAppTerm[] = [];
	public availableTerms: ISortedAppTerm[] = [];

	constructor(
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService,
		private dialogService: DialogService,
		private legalsService: LegalsService,
		private i18nService: I18nService
	) {}

	public ngOnInit(): void {
		this.init(this.terms);
	}

	private init(terms: IAppRequiredTerms): void {
		this.associatedTerms = terms.oAuthApp.map((term) => {
			return {
				...term
			};
		});

		this.mergedTerms = this.associatedTerms.sort((a, b) => {
			return a.displayName.localeCompare(b.displayName);
		});

		this.getTerms();
	}

	public ngAfterViewInit(): void {
		this.sort.sortChange.subscribe((value: { active: string; direction: string }) => {
			this.mergedTerms.sort((a, b) => {
				const aValue = a[value.active] ? a[value.active] : '';
				const bValue = b[value.active] ? b[value.active] : '';

				return value.direction === 'asc'
					? bValue?.localeCompare(aValue)
					: value.direction === 'desc'
					? aValue?.localeCompare(bValue)
					: a.displayName.localeCompare(b.displayName);
			});

			this.table.renderRows();
		});
	}

	public viewTerm(term: ISortedAppTerm): void {
		if (term.documents[term.activeVersion]?.url) {
			this.legalsService.serveExternalDocument(term.documents[term.activeVersion].url);
		} else {
			this.progressIndicatorService.spinner(true);

			this.legalsService.getTermData(term.termsId, term.activeVersion, TermType.app).subscribe(
				(res: HttpResponse<string>) => {
					this.legalsService.serveDocument(
						this.legalsService.getAppTermDocument(term, term.activeVersion),
						res,
						false,
						TermType.app,
						TermState.live,
						null
					);
					this.progressIndicatorService.spinner(false);
				},
				(error: any) => {
					this.feedbackService.apiError(error);
					this.progressIndicatorService.spinner(false);
				}
			);
		}
	}

	public unassociateTerm(term: ISortedAppTerm): void {
		this.feedbackService.confirm({
			title: FeedbackTitle.attention,
			message: this.i18nService.get(
				'This will disassociate the legal document from the app key. Are you sure you want to continue?',
				'app.prompt.legal.disassociate-terms'
			),
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) {
					this.progressIndicatorService.spinner(true);

					this.legalsService.unassociateTermsFromAppKeys(this.appKey.clientId, [term.termsId]).subscribe(
						() => {
							this.appKey.requiredTerms = this.appKey.requiredTerms.filter(
								(requiredTerm) => requiredTerm.termsId !== term.termsId
							);

							this.terms = this.legalsService.data.appKey[this.appKey.clientId];

							this.init(this.terms);

							this.progressIndicatorService.spinner(false);
						},
						(error: any) => {
							this.feedbackService.apiError(error);
							this.progressIndicatorService.spinner(false);
						}
					);
				}
			}
		});
	}

	public associateTerms(): void {
		this.dialogService.open(AppKeyAssociateLegalDialogComponent, {
			data: {
				company: this.company,
				appKey: this.appKey,
				terms: this.availableTerms,
				callback: (res: ISortedAppTerm[]) => {
					this.terms.oAuthApp = res;
					this.init(this.terms);
				}
			}
		});
	}

	private getTerms(): void {
		if (!this.legalsService.data?.app) {
			this.progressIndicatorService.spinner(true);
			this.loading = true;
		}

		this.legalsService.getOathAppLegals(false).subscribe(
			(result: ISortedAppTerm[]) => {
				this.availableTerms = result.filter(
					(term) =>
						this.associatedTerms.findIndex((associatedTerm) => associatedTerm.termsId === term.termsId) === -1 &&
						term?.activeVersion > -1
				);

				this.loading = false;
				this.progressIndicatorService.spinner(false);
			},
			(error: any) => {
				this.feedbackService.apiError(error);
				this.loading = false;
				this.progressIndicatorService.spinner(false);
			}
		);
	}
}
